/**
 * Client-side preflight for document uploads. Server is the security
 * boundary, but mirroring its limits in the UI gives users a fast-fail
 * loop and avoids 50MB+ network round-trips for files the backend will
 * reject anyway.
 *
 * Mirrors the backend's accepted set in `backend/src/services/storage`
 * (PDFs, slides, audio, plaintext, markdown).
 */

/** MIME allowlist for the hidden file inputs. */
export const ACCEPTED_UPLOAD_MIME = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.ms-powerpoint', // .ppt (legacy)
  'audio/*',
  'text/plain',
  'text/markdown',
].join(',')

/** Per-file cap. Backend currently rejects >50MB on the multipart endpoint. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024

/** Human-readable cap for error copy. */
export const MAX_UPLOAD_LABEL = '50MB'

const ACCEPTED_EXTENSIONS = [
  'pdf',
  'pptx',
  'ppt',
  'mp3',
  'wav',
  'm4a',
  'webm',
  'txt',
  'md',
]

function hasAcceptedShape(file: File): boolean {
  if (file.size === 0) return false // 0-byte files = empty drop / folder phantom
  if (file.type) {
    if (file.type.startsWith('audio/')) return true
    if (
      file.type === 'application/pdf' ||
      file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      file.type === 'application/vnd.ms-powerpoint' ||
      file.type === 'text/plain' ||
      file.type === 'text/markdown'
    ) {
      return true
    }
  }
  // Fall back to extension check — some browsers / drag sources don't set MIME.
  const ext = file.name.toLowerCase().split('.').pop() ?? ''
  return ACCEPTED_EXTENSIONS.includes(ext)
}

export interface PartitionResult {
  accepted: File[]
  rejected: { file: File; reason: string }[]
}

/**
 * Split a file list into accepted vs rejected. Reasons are user-facing
 * one-line strings; UI surfaces the first reason on a multi-rejection.
 */
export function partitionUploadFiles(files: File[]): PartitionResult {
  const accepted: File[] = []
  const rejected: { file: File; reason: string }[] = []
  for (const file of files) {
    if (file.size === 0) {
      rejected.push({ file, reason: `${file.name} looks empty.` })
      continue
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      rejected.push({
        file,
        reason: `${file.name} is over the ${MAX_UPLOAD_LABEL} limit.`,
      })
      continue
    }
    if (!hasAcceptedShape(file)) {
      rejected.push({
        file,
        reason: `${file.name} isn't a supported type. Try PDF, slides, audio, or text.`,
      })
      continue
    }
    accepted.push(file)
  }
  return { accepted, rejected }
}
